/**
 * @file: index.ts
 * @author: eric <xuxiang@zhichetech.com>
 * @copyright: (c) 2019-2020 sichuan zhichetech co., ltd.
 */

import { StatusColors } from '@euler/components/Colors';
import { FontFamily } from '@euler/components/typography';
import { getDefectiveLevelColor } from '@euler/functions/getDefectiveLevel';
import { DefectiveLevel } from '@euler/model';
import { AttributedText } from '@euler/model/AttributedText';
import {
  InspectionTaskEventSubType,
  InspectionTaskEventType,
  SamplingCheckResultType,
} from '@euler/model/enum';
import { nanoid } from 'nanoid/non-secure';
import { DefaultTheme } from 'styled-components';
import { registerEventType } from '../../registry';

const register = registerEventType(InspectionTaskEventType.SamplingCheck);

function itemReviewed() {
  register(InspectionTaskEventSubType.SamplingCheckListItemReviewed)(
    (item, data) => {
      item.label(data.name).medias('m');

      const tagText = [data.text, data.remark]
        .filter(x => x?.trim())
        .join(': ');
      if (tagText) {
        const color = (theme: DefaultTheme) =>
          getDefectiveLevelColor(
            theme,
            data.code === 'OK' ? DefectiveLevel.Fine : DefectiveLevel.Warning,
          );
        const tagline: (string | AttributedText)[] = [
          {
            text:
              (data.code === 'OK' ? '✅ ' : '❌ ') +
              tagText +
              (data.ur ? ` (${data.ur})` : ''),
            color,
          },
        ];
        item.tagline(tagline);
      }

      if (data.rr?.length) {
        for (const record of data.rr) {
          const builder = item.clone();
          builder
            .key(nanoid())
            .label('👆内返记录👆')
            .medias('m', record)
            .tagline(`责任人: ${record.rp}`)
            .tagline(record.re ? `备注: ${record.re}` : '')
            .author('');
          item.associated(builder.get());
        }
      }
    },
  );
}

function reportGenerated() {
  register(InspectionTaskEventSubType.SamplingCheckReportGenerated)(item => {
    item.label({
      text: '报告已生成',
      color: StatusColors.Success,
      fontFamily: FontFamily.NotoSans.Medium,
    });
  });
}

function started() {
  register(InspectionTaskEventSubType.SamplingCheckStarted)(item => {
    item.label('开始完工抽检');
  });
}

function finished() {
  register(InspectionTaskEventSubType.SamplingCheckFinished)((item, data) => {
    const t = data.t as SamplingCheckResultType;
    item
      .label({
        text: '抽检完成',
        fontFamily: FontFamily.NotoSans.Medium,
        color: StatusColors.Info,
      })
      .tagline({
        fontFamily: FontFamily.NotoSans.Medium,
        text:
          t === SamplingCheckResultType.Passed
            ? '✅ 检验合格，允许交车'
            : '🚫 检验不合格，禁止交车',
        color:
          t === SamplingCheckResultType.Passed
            ? StatusColors.Success
            : StatusColors.Danger,
      });
  });
}

function amendRequested() {
  register(InspectionTaskEventSubType.SamplingCheckReportAmendRequested)(
    item => {
      item.label({
        text: '修改报告',
        color: StatusColors.Info,
        fontFamily: FontFamily.NotoSans.Medium,
      });
    },
  );
}

function reportUpdated() {
  register(InspectionTaskEventSubType.SamplingCheckReportUpdated)(item => {
    item.label({
      text: '报告已更新',
      color: StatusColors.Info,
      fontFamily: FontFamily.NotoSans.Medium,
    });
  });
}

started();
itemReviewed();
reportGenerated();
finished();
amendRequested();
reportUpdated();
